import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
  retry,
} from "@reduxjs/toolkit/query/react";

import { getToken } from "../../utils/session";
import { TOKEN } from "../../modules/auth/AuthHelper";
import * as Sentry from "@sentry/react";
import { appNotification, showSessionExpiredModal } from "../../app.slice";
import { PUBLIC_ROUTES } from "../../routing/routePaths";

// Create our baseQuery instance
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_APP_API_URL,
  prepareHeaders: (headers, { getState }) => {
    // By default, if we have a token in the store, let's use that for authenticated requests
    const token = getToken(TOKEN);
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error) {
    // Log error to Sentry with additional context
    Sentry.captureException(result.error, {
      extra: {
        status: result.error.status,
        endpoint: typeof args === "string" ? args : args.url,
        method: typeof args === "string" ? "GET" : args.method,
        errorData: JSON.stringify(result.error),
      },
      tags: {
        type: "api_error",
        status: result.error.status?.toString(),
      },
    });

    if (result.error.status === 401) {
      try {
        // Get current state to check if modal is already shown
        const state = api.getState() as any;
        const isSessionModalAlreadyShown = state.app?.showSessionExpiredModal;

        if (!isSessionModalAlreadyShown) {
          Sentry.captureException(result.error, {
            extra: {
              originalError: result.error,
              context: "401_handling",
            },
          });
          // Show session expired modal only if not already shown
          api.dispatch(showSessionExpiredModal());
        }
      } catch (error) {
        console.log(error);
        // Log the error that occurred during 401 handling
        Sentry.captureException(error, {
          extra: {
            originalError: result.error,
            context: "401_handling_catch",
          },
        });
      }
    }

    if (result.error.status === 403) {
      try {
        const isAlreadyOnForbiddenPage = window.location.pathname.includes(PUBLIC_ROUTES.ERROR_403);

        if (!isAlreadyOnForbiddenPage) {
          api.dispatch(
            appNotification({
              type: "danger",
              msg: (result.error.data as { error?: string })?.error || "You don't have permission to access this resource.",
              title: "Access Denied",
              notificationType: "modal",
            })
          );
          Sentry.captureException(result.error, {
            extra: {
              originalError: result.error,
              context: "403_handling",
            },
          });
          window.location.href = PUBLIC_ROUTES.ERROR_403;
        }
      } catch (error) {
        console.log(error);
        Sentry.captureException(error, {
          extra: {
            originalError: result.error,
            context: "403_handling_catch",
          },
        });
      }
    }
  }
  return result;
};

const baseQueryWithRetry = retry(baseQueryWithReauth, { maxRetries: 1 });

/**
 * Create a base API to inject endpoints into elsewhere.
 * Components using this API should import from the injected site,
 * in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded
 */
export const api = createApi({
  /**
   * `reducerPath` is optional and will not be required by most users.
   * This is useful if you have multiple API definitions,
   * e.g. where each has a different domain, with no interaction between endpoints.
   * Otherwise, a single API definition should be used in order to support tag invalidation,
   * among other features
   */
  reducerPath: "api",
  /**
   * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
   */
  baseQuery: baseQueryWithRetry,
  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints
   */
  tagTypes: ["Auth", "Modules", "Module_Path", "Navigation", "Customers", "SidebarCounter"],
  /**
   * This api has endpoints injected in adjacent files,
   * which is why no endpoints are shown below.
   * If you want all endpoints defined in the same file, they could be included here instead
   */
  endpoints: () => ({}),
});
